import { useEffect, useState } from 'react';
import { Card, Col, CardBody, CardFooter, Table, Input, Badge } from 'reactstrap';
import { H6, Btn, Spinner } from 'AbstractElements';
import { useDispatch, useSelector } from 'react-redux';
import {
	setSelectedIds,
	toggleSelectedId,
	clearSelectedIds,
} from 'Features/Learning/ToolLeads/toolLeadSlice';
import PaginationComponent from '../Common/Pagination';
import ModalDetailToolLead from './ModalDetailToolLead';
import {
	fetchToolLeadList,
	handleDeleteToolLead,
	handleBackPagination,
	handleNextPagination,
	handleClickPagination,
	TOOL_LABELS,
	formatDateTime,
	formatWhatsappDisplay,
	buildWhatsappLink,
} from './ToolLeadUtils';

const ToolLeadBox = () => {
	const dispatch = useDispatch();
	const filters = useSelector((state) => state.ToolLead.filters);
	const isLoading = useSelector((state) => state.ToolLead.isLoading);
	const cards = useSelector((state) => state.ToolLead.data?.data);
	const pagination = useSelector((state) => state.ToolLead.data?.pagination);
	const selectedIds = useSelector((state) => state.ToolLead.selectedIds);
	const [refetch, setRefetch] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [detailOpen, setDetailOpen] = useState(false);
	const [detailId, setDetailId] = useState(null);

	const totalData = pagination?.total ?? 0;
	const isCardsReady = Array.isArray(cards);
	const emptyStateMessage = isCardsReady ? 'Data Tidak Ditemukan' : 'Sumber Data Tidak Tersedia';

	useEffect(() => {
		fetchToolLeadList(dispatch, filters);
		setHasFetched(true);
	}, [dispatch, filters, refetch]);

	const pageIds = isCardsReady ? cards.map((item) => item.id) : [];
	const allPageSelected =
		pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

	const handleToggleAll = () => {
		if (allPageSelected) {
			dispatch(setSelectedIds(selectedIds.filter((id) => !pageIds.includes(id))));
		} else {
			const merged = [...new Set([...selectedIds, ...pageIds])];
			dispatch(setSelectedIds(merged));
		}
	};

	const openDetail = (id) => {
		setDetailId(id);
		setDetailOpen(true);
	};

	const closeDetail = () => {
		setDetailOpen(false);
		setDetailId(null);
	};

	return (
		<>
			{isLoading ? (
				<Col lg="12">
					<div className="loader-box">
						<Spinner attrSpinner={{ className: 'loader-30' }} />
					</div>
					<H6 attrH6={{ className: 'sub-title mb-0 text-center' }}>Data Loading...</H6>
				</Col>
			) : (
				<>
					{isCardsReady && cards.length > 0 && (
						<Col sm="12 order-2">
							<Card>
								<CardBody className="table-responsive">
									<div className="d-flex justify-content-between align-items-center mb-3">
										<H6 attrH6={{ className: 'mb-0' }}>{`Total data: ${totalData}`}</H6>
										{selectedIds.length > 0 && (
											<Btn
												attrBtn={{
													color: 'light',
													size: 'sm',
													onClick: () => dispatch(clearSelectedIds()),
												}}
											>
												Bersihkan pilihan ({selectedIds.length})
											</Btn>
										)}
									</div>
									<Table className="table-bordered table-striped table-hover">
										<thead>
											<tr>
												<th scope="col" className="bg-primary text-light text-center" style={{ width: 40 }}>
													<Input
														type="checkbox"
														checked={allPageSelected}
														onChange={handleToggleAll}
													/>
												</th>
												<th scope="col" className="bg-primary text-light">#</th>
												<th scope="col" className="bg-primary text-light">Nama</th>
												<th scope="col" className="bg-primary text-light">WhatsApp</th>
												<th scope="col" className="bg-primary text-light">Email</th>
												<th scope="col" className="bg-primary text-light">Tools</th>
												<th scope="col" className="bg-primary text-light">Percobaan</th>
												<th scope="col" className="bg-primary text-light">Daftar</th>
												<th scope="col" className="bg-primary text-light">Update</th>
												<th scope="col" className="bg-primary text-light text-center">Aksi</th>
											</tr>
										</thead>
										<tbody>
											{cards.map((item, index) => {
												const waLink = buildWhatsappLink(item.whatsapp, item.name);
												return (
													<tr key={`tool-lead-${item.id}`} className="table-light">
														<td className="text-center">
															<Input
																type="checkbox"
																checked={selectedIds.includes(item.id)}
																onChange={() => dispatch(toggleSelectedId(item.id))}
															/>
														</td>
														<th>{(pagination?.from ?? 1) + index}</th>
														<td>
															<strong>{item.name || '-'}</strong>
														</td>
														<td>
															{waLink ? (
																<a href={waLink} target="_blank" rel="noreferrer">
																	{formatWhatsappDisplay(item.whatsapp)}
																</a>
															) : (
																'-'
															)}
														</td>
														<td>{item.email || '-'}</td>
														<td>
															<Badge color="secondary">
																{TOOL_LABELS[item.tools_name] || item.tools_name}
															</Badge>
														</td>
														<td className="text-center">{item.attempts ?? 0}</td>
														<td>{formatDateTime(item.created_at)}</td>
														<td>{formatDateTime(item.updated_at)}</td>
														<td className="text-center">
															<div className="d-flex justify-content-center gap-1">
																<Btn
																	attrBtn={{
																		color: 'info',
																		size: 'sm',
																		onClick: () => openDetail(item.id),
																	}}
																>
																	Detail
																</Btn>
																<Btn
																	attrBtn={{
																		color: 'danger',
																		size: 'sm',
																		onClick: () =>
																			handleDeleteToolLead(dispatch, item.id, setRefetch),
																	}}
																>
																	Hapus
																</Btn>
															</div>
														</td>
													</tr>
												);
											})}
										</tbody>
									</Table>
								</CardBody>
								{pagination && (
									<CardFooter>
										<PaginationComponent
											pagination={pagination}
											handleBackPagination={() =>
												handleBackPagination(pagination, filters, dispatch, setRefetch)
											}
											handleNextPagination={() =>
												handleNextPagination(pagination, filters, dispatch, setRefetch)
											}
											handleClickPagination={(page) =>
												handleClickPagination(page, filters, dispatch, setRefetch)
											}
										/>
									</CardFooter>
								)}
							</Card>
						</Col>
					)}

					{hasFetched && (!isCardsReady || cards.length === 0) && (
						<Col lg="12">
							<div className="text-center py-5">
								<H6 attrH6={{ className: 'mb-0' }}>{emptyStateMessage}</H6>
							</div>
						</Col>
					)}
				</>
			)}

			<ModalDetailToolLead
				isOpen={detailOpen}
				toggle={closeDetail}
				leadId={detailId}
			/>
		</>
	);
};

export default ToolLeadBox;
